import { useState, useCallback, type ReactNode, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface PaymentSetupStepProps {
  readonly onComplete: (data: { merchantId: string; apiKey: string }) => void;
}

export function PaymentSetupStep({ onComplete }: PaymentSetupStepProps): ReactNode {
  const [merchantId, setMerchantId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<"success" | "error" | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleTest = useCallback(async () => {
    const newErrors: Record<string, string> = {};
    if (!merchantId.trim()) newErrors["merchantId"] = "\u05E9\u05D3\u05D4 \u05D7\u05D5\u05D1\u05D4";
    if (!apiKey.trim()) newErrors["apiKey"] = "\u05E9\u05D3\u05D4 \u05D7\u05D5\u05D1\u05D4";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setTesting(true);
    setTestResult(null);
    // Simulated test — in production this pings Grow API
    await new Promise((r) => setTimeout(r, 1500));
    setTestResult("success");
    setTesting(false);
  }, [merchantId, apiKey]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (testResult !== "success") return;
      onComplete({ merchantId: merchantId.trim(), apiKey: apiKey.trim() });
    },
    [merchantId, apiKey, testResult, onComplete],
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D0\u05DE\u05E6\u05E2\u05D9 \u05EA\u05E9\u05DC\u05D5\u05DD"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D7\u05D1\u05E8\u05D5 \u05D0\u05EA \u05D7\u05E9\u05D1\u05D5\u05DF Grow \u05E9\u05DC\u05DB\u05DD \u05DC\u05E7\u05D1\u05DC\u05EA \u05EA\u05E9\u05DC\u05D5\u05DE\u05D9\u05DD \u05D1\u05D0\u05EA\u05E8."}
      </p>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="merchantId" className="text-sm font-medium">
              Merchant ID
            </label>
            <Input
              id="merchantId"
              dir="ltr"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder="GROW-XXXXX"
            />
            {errors["merchantId"] !== undefined && (
              <p className="text-sm text-destructive">{errors["merchantId"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="apiKey" className="text-sm font-medium">
              API Key
            </label>
            <Input
              id="apiKey"
              dir="ltr"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder="\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"
            />
            {errors["apiKey"] !== undefined && (
              <p className="text-sm text-destructive">{errors["apiKey"]}</p>
            )}
          </div>

          <Button
            type="button"
            variant="outline"
            className="min-h-[44px]"
            disabled={testing}
            onClick={handleTest}
          >
            {testing ? "\u05D1\u05D5\u05D3\u05E7..." : "\u05D1\u05D3\u05D5\u05E7 \u05D7\u05D9\u05D1\u05D5\u05E8"}
          </Button>

          {testResult === "success" && (
            <p className="text-sm font-medium text-green-600">{"\u05D4\u05D7\u05D9\u05D1\u05D5\u05E8 \u05EA\u05E7\u05D9\u05DF!"}</p>
          )}
          {testResult === "error" && (
            <p className="text-sm text-destructive">{"\u05D4\u05D7\u05D9\u05D1\u05D5\u05E8 \u05E0\u05DB\u05E9\u05DC. \u05D1\u05D3\u05E7\u05D5 \u05D0\u05EA \u05D4\u05E4\u05E8\u05D8\u05D9\u05DD."}</p>
          )}

          <a
            href="https://www.grow.co.il/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary underline"
          >
            {"\u05D0\u05D9\u05DF \u05DC\u05DB\u05DD \u05D7\u05E9\u05D1\u05D5\u05DF Grow? \u05D4\u05D9\u05E8\u05E9\u05DE\u05D5 \u05DB\u05D0\u05DF"}
          </a>
        </CardContent>
      </Card>

      <Button
        type="submit"
        size="lg"
        className="min-h-[44px]"
        disabled={testResult !== "success"}
      >
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </form>
  );
}
